import { ComponentType, FC, SVGAttributes, useMemo } from "react";
import {
  Alert,
  Badge,
  BadgeProps,
  Box,
  Column,
  ExternalLinkIcon,
  Heading,
  IconBox,
  InformationIcon,
  LightningBoltIcon,
  QuestionIcon,
  Row,
  Skeleton,
  SkeletonBox,
  Spinner,
  Text,
  Tooltip,
  TraitIcon,
  TraitTemplateIcon,
  TextWithTooltip,
  HtImage,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isRealtimeEventModel } from "src/utils/event-models";
import genericPlaceholder from "src/assets/placeholders/generic.svg";
import modelPlaceholder from "src/assets/placeholders/model.svg";

import {
  EventColumn,
  FilterOption,
  RealtimeEventColumn,
  RelationColumn,
} from "./constants";
import { FilterColumnOption, TraitFilterOption } from "./types";
import { useFetchSuggestions } from "src/hooks/use-column-suggestions";
import { Link } from "src/router";
import times from "lodash/times";
import {
  getModelIdFromColumn,
  getPropertyNameFromColumn,
} from "src/components/explore/visual/utils";
import { CalculationSummary } from "src/components/traits/calculation-summary";
import { stringify } from "src/utils/string";
import { abbreviateNumber } from "src/utils/numbers";
import { TRAIT_TYPE_LABELS } from "src/components/traits/utils";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { useMinimalSyncsForSegmentQuery } from "src/graphql";
import { getSemanticTypeTooltip } from "src/components/audiences/utils";
import { createSchemaObjectLink } from "src/pages/schema/view/utils";
import { useHydrateTraitsWithFilterableAudienceColumns } from "src/hooks/use-fetch-traits-with-filterable-audience-columns";
import { JourneyStatusBadge } from "src/pages/journeys/components/journey-status-badge";
import { diff } from "src/utils/time";
import { getColumnTypeMetadata } from "./utils";
import { TraitType } from "src/types/visual";
import { SemanticTypeIcons } from "src/pages/schema/graph/schema-icon-maps";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { useFilterableOptionsContext } from "./filterable-options-hook";
import {
  getRtaMembershipReferenceDisabledMessage,
  getSourceSettingsPath,
} from "src/components/audiences/rta-membership-reference";

type FilterPopoverOptionMetadataProps = {
  option: FilterColumnOption | null | undefined;
  sourceId: string;
  /**
   * Hides the "% of audiences using" line. That metric describes how often a
   * column is used as an audience filter, which is meaningless in contexts that
   * reuse this panel to preview an audience itself (e.g. the allowlist picker).
   */
  hideUsagePercentage?: boolean;
};

export const FilterPopoverOptionMetadata = ({
  option,
  sourceId,
  hideUsagePercentage = false,
}: FilterPopoverOptionMetadataProps) => {
  const { appHouseholdingEnabled } = useFlags();
  const { parentConnectionId } = useFilterableOptionsContext();
  const { journeyTraits } = useQueryBuilderContext();
  const modelId = getModelIdFromColumn(option?.value);
  const columnName = getPropertyNameFromColumn(option?.value);
  const { loadingSuggestions, suggestions } = useFetchSuggestions(
    {
      modelId: modelId ?? undefined,
      columnName: columnName ?? undefined,
      traitId:
        option && option.type === FilterOption.Trait
          ? option?.traitId
          : undefined,
    },
    {
      // The hook checks that modelId and columnName are defined, so we don't need to do it here
      enabled:
        (option?.type === FilterOption.Property ||
          option?.type === FilterOption.Trait) &&
        option.suggestionsEnabled,
    },
  );

  const suggestionFragment = useMemo(() => {
    if (
      !option ||
      (option?.type !== FilterOption.Property &&
        option?.type !== FilterOption.Trait &&
        option?.type !== FilterOption.TraitTemplate)
    ) {
      return null;
    }

    if (!option.suggestionsEnabled || suggestions.length === 0) {
      return null;
    }
    const topSuggestions = suggestions.slice(0, 5);
    return (
      <SimpleTable
        header="Most common values"
        rows={topSuggestions}
        showPlaceholder={loadingSuggestions}
      />
    );
  }, [option, suggestions, loadingSuggestions]);

  const { data: syncs = [], isLoading: isSyncsLoading } =
    useMinimalSyncsForSegmentQuery(
      {
        segmentId: option?.value?.modelId,
      },
      {
        enabled:
          option?.type === FilterOption.Audience &&
          option?.value?.modelId != null,
        select: (data) => data.syncs,
      },
    );

  if (!option) {
    return (
      <Column alignItems="center" height="100%" justifyContent="center">
        <HtImage src={modelPlaceholder} decorative width={48} />
        <Text fontWeight="semibold" color="text.secondary">
          No filter selected
        </Text>
        <Text color="text.tertiary" mt={1}>
          Hover on a filter to preview details
        </Text>
      </Column>
    );
  }

  const usagePercentage = hideUsagePercentage ? null : (
    <UsagePercentage ratio={option.usageRatio} />
  );

  if (option.type === FilterOption.JourneyMemberState) {
    return (
      <Column>
        <Heading size="md">{option.label}</Heading>
        <Text color="text.secondary">{option.description}</Text>
      </Column>
    );
  }

  if (option.type === FilterOption.Property) {
    const columnTypeMetadata = getColumnTypeMetadata(option.propertyType);
    const ColumnTypeIcon = columnTypeMetadata?.icon ?? QuestionIcon;

    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          labelUrl={createSchemaObjectLink(sourceId, modelId ?? "", "columns")}
          labelTooltip="Open in schema builder"
          description={option.description}
          icon={ColumnTypeIcon}
          iconTooltip={columnTypeMetadata?.label}
        />
        {usagePercentage}
        {getManagedPropertyFragment(option)}
        {suggestionFragment}
      </Column>
    );
  }

  if (option.type === FilterOption.Relation) {
    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          labelUrl={createSchemaObjectLink(sourceId, option.toModelId, "query")}
          labelTooltip="Open in schema builder"
          description={option.description}
          icon={
            appHouseholdingEnabled && option.semanticType
              ? SemanticTypeIcons[option.semanticType]
              : undefined
          }
          iconTooltip={
            appHouseholdingEnabled &&
            getSemanticTypeTooltip(option.semanticType)
          }
          iconVariant="clear"
        />
        {usagePercentage}
        {getManagedPropertyFragment(option)}
        <SimpleTable
          header="Example columns"
          rows={option.examplePropertyNames}
        />
      </Column>
    );
  }

  if (
    option.type === FilterOption.Event ||
    option.type === FilterOption.EventProperty ||
    option.type === FilterOption.RealtimeEvent ||
    option.type === FilterOption.MessageEvent
  ) {
    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          badge={
            option.type === FilterOption.RealtimeEvent
              ? {
                  icon: LightningBoltIcon,
                  label: "Realtime event",
                  variant: "info",
                }
              : undefined
          }
          labelUrl={createSchemaObjectLink(
            sourceId,
            "eventModelId" in option.value
              ? option.value.eventModelId
              : option.value.modelId,
            "query",
          )}
          labelTooltip="Open in schema builder"
          description={option.description}
          icon={
            appHouseholdingEnabled &&
            "semanticType" in option &&
            option.semanticType
              ? SemanticTypeIcons[option.semanticType]
              : undefined
          }
          iconTooltip={
            appHouseholdingEnabled && "semanticType" in option
              ? getSemanticTypeTooltip(option.semanticType)
              : undefined
          }
          iconVariant="clear"
        />
        {option.isDisabled ? (
          <Alert
            variant="inline"
            type="subtle"
            title="Realtime events cannot be added to an existing audience. Create a new audience to utilize realtime events."
          />
        ) : (
          <>
            {usagePercentage}
            <SimpleTable
              header="Example columns"
              rows={option.examplePropertyNames.filter(Boolean)}
            />
          </>
        )}
      </Column>
    );
  }

  if (option.type === FilterOption.Audience) {
    const disabledMessage = getRtaMembershipReferenceDisabledMessage(
      option.disabledReason,
    );

    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          labelUrl={`/audiences/${option.value.modelId}`}
          labelTooltip="Open audience details"
          description={option.description}
          badge={option.badge}
        />
        {usagePercentage}

        {option.isDisabled && disabledMessage ? (
          <Alert
            variant="inline"
            type="subtle"
            title={disabledMessage}
            actions={
              option.disabledReason === "snapshotting_disabled" &&
              parentConnectionId ? (
                <Link
                  href={getSourceSettingsPath(parentConnectionId)}
                  isExternal
                >
                  Open source settings
                </Link>
              ) : undefined
            }
          />
        ) : null}

        <Column gap={2}>
          <Text color="text.secondary" fontWeight="medium">
            Sending to
          </Text>
          {isSyncsLoading ? (
            <Skeleton isLoading={isSyncsLoading}>
              <Row gap={1.5}>
                {times(3).map((i) => (
                  <SkeletonBox key={i} w="30px" h="30px" borderRadius="50%" />
                ))}
              </Row>
            </Skeleton>
          ) : syncs.length ? (
            <SimpleSyncCells syncs={syncs} />
          ) : (
            <Text color="text.secondary">No syncs</Text>
          )}
        </Column>
      </Column>
    );
  }

  if (option.type === FilterOption.InlineTrait) {
    return (
      <Column
        alignItems="center"
        justifyContent="center"
        height="100%"
        p={4}
        textAlign="center"
      >
        <HtImage src={genericPlaceholder} decorative />
        <Text color="text.secondary" fontWeight="semibold">
          {option.label}
        </Text>
      </Column>
    );
  }

  if (
    option.type === FilterOption.Trait ||
    option.type === FilterOption.TraitTemplate
  ) {
    const hasRelatedModel = Boolean(option.relatedModel);
    const isEventModel =
      option.relatedModel && Boolean(option.relatedModel.event);
    const isRealtimeEvent = isRealtimeEventModel(option.relatedModel?.event);
    const isPredictiveTrait = option.traitType === TraitType.Predictive;

    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          labelUrl={
            // The traits list filters out journey traits; open those in the
            // journey's own trait drawer instead.
            journeyTraits?.some(({ id }) => id === option.traitId)
              ? `?view=trait&traitId=${option.traitId}`
              : `/traits/${option.type === FilterOption.Trait ? "active" : "templates"}/${option.traitId}`
          }
          labelTooltip="Open trait details"
          description={option.description}
          iconTooltip={
            option.type === FilterOption.Trait ? "Trait" : "Trait template"
          }
          icon={
            option.type === FilterOption.Trait ? TraitIcon : TraitTemplateIcon
          }
        />
        {usagePercentage}
        {hasRelatedModel && !isPredictiveTrait && (
          <Column gap={1}>
            <Text color="text.secondary" fontWeight="medium">
              Based on
            </Text>
            <Row alignItems="center" gap={1}>
              <IconBox
                size="xs"
                color={isEventModel ? EventColumn.shade : RelationColumn.shade}
                icon={
                  isEventModel
                    ? isRealtimeEvent
                      ? RealtimeEventColumn.icon
                      : EventColumn.icon
                    : RelationColumn.icon
                }
              />
              <Text>{option.relatedModel?.name}</Text>
            </Row>
          </Column>
        )}
        <Column gap={1}>
          <Text color="text.secondary" fontWeight="medium">
            Type
          </Text>
          <Text>{TRAIT_TYPE_LABELS[option.traitType]}</Text>
        </Column>
        {option.parentModel && (
          <Column gap={1}>
            <Text color="text.secondary" fontWeight="medium">
              Definition
            </Text>
            <FilterPopoverCalculationSummary {...option} />
          </Column>
        )}
        {suggestionFragment}
      </Column>
    );
  }

  if (option.type === FilterOption.Journey) {
    const lastRunText = option.stats.lastRunFinishedAt
      ? diff(new Date().toISOString(), option.stats.lastRunFinishedAt) + " ago"
      : "Never run";
    return (
      <Column gap={6}>
        <OptionHeader
          label={option.label}
          labelUrl={`/journeys/${option.value.journeyId}`}
          labelTooltip="Open journey details"
          description={option.description}
        />
        {usagePercentage}

        <Column gap={1} alignItems="flex-start">
          <Text color="text.secondary" fontWeight="medium">
            Status
          </Text>
          <JourneyStatusBadge status={option.state} />
        </Column>

        <Column>
          <Text color="text.secondary" fontWeight="medium" mb={1}>
            Members
          </Text>
          <Text color="text.primary">
            {option.stats.numberOfUsersInJourney} In progress
          </Text>
          <Text color="text.primary">
            {option.stats.journeyEntryCount} Entered
          </Text>
          <Text color="text.primary">
            {option.stats.journeyExitCount} Met exit criteria
          </Text>
          <Text color="text.primary">
            {option.stats.journeyCompletedCount} Finished
          </Text>
        </Column>

        <Column gap={1}>
          <Text color="text.secondary" fontWeight="medium">
            Last Run
          </Text>
          <Text color="text.primary">{lastRunText}</Text>
        </Column>

        {option.syncs.length > 0 && (
          <Column gap={2}>
            <Text color="text.secondary" fontWeight="medium">
              Sending to
            </Text>
            <SimpleSyncCells syncs={option.syncs} />
          </Column>
        )}
      </Column>
    );
  }

  return null;
};

const FilterPopoverCalculationSummary: FC<TraitFilterOption> = ({
  parentModel,
  relatedModel,
  traitConfig,
  traitType,
  traitPredictor,
}) => {
  const { traitsWithFilterableColumns, isLoading } =
    useHydrateTraitsWithFilterableAudienceColumns(parentModel?.traits ?? []);

  const parentModelWithFilterableTraitColumns = useMemo(() => {
    if (!parentModel) {
      return undefined;
    }

    return {
      ...parentModel,
      traits: traitsWithFilterableColumns,
    };
  }, [parentModel, traitsWithFilterableColumns]);

  if (!parentModel) {
    return null;
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box overflowX="auto">
      <Box width="fit-content">
        <CalculationSummary
          hideModel
          parentModel={parentModelWithFilterableTraitColumns}
          relatedModel={relatedModel}
          config={traitConfig}
          type={traitType}
          predictor={traitPredictor}
        />
      </Box>
    </Box>
  );
};

export const OptionHeader = ({
  label,
  labelUrl,
  labelTooltip,
  description,
  icon,
  iconTooltip,
  iconVariant = "default",
  badge,
}: {
  label: string;
  labelUrl: string;
  labelTooltip: string;
  description?: string | null;
  icon?: React.ComponentType<React.SVGAttributes<SVGElement>>;
  iconTooltip?: string;
  iconVariant?: "default" | "clear";
  badge?: {
    icon?: ComponentType<SVGAttributes<SVGElement>>;
    label: string;
    variant: BadgeProps["variant"];
  };
}) => (
  <Column>
    <Box
      display="grid"
      // The second column (for the icon) is set to max-content, so that
      // a long label (first column) doesn't squish it.
      gridTemplateColumns="1fr max-content"
      justifyContent="space-between"
      gap={2}
    >
      <Tooltip message={labelTooltip}>
        <Row
          as={Link}
          isExternal
          href={labelUrl}
          gap={1}
          fontSize={20}
          overflow="hidden"
        >
          <Box as={Heading} overflowWrap="anywhere" size="md">
            {label} <Box as={ExternalLinkIcon} color="black" />
          </Box>
        </Row>
      </Tooltip>

      {icon && (
        <Tooltip message={iconTooltip ?? ""}>
          <Row
            backgroundColor={
              iconVariant === "default" ? "base.background" : "transparent"
            }
            justifyContent="center"
            borderRadius="50%"
            color="text.secondary"
            h={6}
            w={6}
          >
            <Box
              as={icon}
              h={iconVariant === "default" ? 4 : 5}
              w={iconVariant === "default" ? 4 : 5}
              flexShrink={0}
            />
          </Row>
        </Tooltip>
      )}
    </Box>
    {description && <Text color="text.secondary">{description}</Text>}
    {badge && (
      <Badge mt={1} svgIcon={badge.icon} variant={badge.variant}>
        {badge.label}
      </Badge>
    )}
  </Column>
);

const UsagePercentage = ({ ratio }: { ratio: number | undefined }) => (
  <Column>
    <Text fontWeight="medium" size="lg">
      {formatRatioAsPercentage(ratio ?? 0)}
    </Text>
    <Text color="text.secondary" size="sm">
      of audiences using
    </Text>
  </Column>
);

const PercentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
});

// Ratio is a number between [0, 1]
const formatRatioAsPercentage = (ratio: number): string => {
  // Special-case for really small values
  if (ratio > 0 && ratio < 0.01) {
    return "<1%";
  }

  return PercentFormatter.format(ratio);
};

export const SimpleTable = ({
  header,
  rows,
  showPlaceholder,
}: {
  header: string;
  rows:
    | { value: string | number | boolean | null; count: number | undefined }[]
    | string[];
  showPlaceholder?: boolean;
}) => {
  return (
    <Column gap={2}>
      <Text color="text.secondary" fontWeight="medium">
        {header}
      </Text>
      <Column>
        <>
          {showPlaceholder && (
            <Skeleton isLoading={showPlaceholder}>
              <Column gap={2}>
                {times(5).map((i) => (
                  <SkeletonBox key={i} height={5} w="100%" />
                ))}
              </Column>
            </Skeleton>
          )}

          {rows.map((row: (typeof rows)[number], index: number) => (
            <Row
              key={typeof row === "string" ? row : row.value?.toString()}
              gap={2}
              justifyContent="space-between"
              {...(index === 0
                ? { borderY: "var(--chakra-border-hairline) solid" }
                : {
                    borderBottomWidth: "hairline",
                    borderBottomStyle: "solid",
                  })}
              borderColor="base.divider"
              py={2}
              pl={1}
            >
              {typeof row === "string" ? (
                <TextWithTooltip>{stringify(row)}</TextWithTooltip>
              ) : (
                <>
                  <TextWithTooltip>{stringify(row.value)}</TextWithTooltip>
                  {row.count != null && (
                    <Text>{abbreviateNumber(row.count)}</Text>
                  )}
                </>
              )}
            </Row>
          ))}
        </>
      </Column>
    </Column>
  );
};

const SimpleSyncCells = (props: {
  syncs: {
    id: string;
    destination: {
      name: string | null;
      definition: {
        icon: string;
        name: string;
      };
    } | null;
  }[];
}) => (
  <Row align="center" w="100%" h="100%" flexWrap="wrap" gap={1.5}>
    {props.syncs.map((sync) => (
      <Tooltip
        key={sync.id}
        message={
          sync.destination?.name ??
          sync.destination?.definition?.name ??
          "Private destination"
        }
      >
        <Link href={`/syncs/${sync.id}`} isExternal>
          <Row
            align="center"
            justify="center"
            w="30px"
            h="30px"
            borderRadius="50%"
            bg="white"
            borderWidth="hairline"
            borderStyle="solid"
            borderColor="base.border"
          >
            <IntegrationIcon
              size={4}
              name={sync.destination?.definition?.name}
              src={sync.destination?.definition?.icon}
            />
          </Row>
        </Link>
      </Tooltip>
    ))}
  </Row>
);

const getManagedPropertyFragment = (option: {
  propertyType?: string;
  isHightouchManaged?: boolean;
}) => {
  if (!option.isHightouchManaged) {
    return null;
  }

  return (
    <Column>
      <Row gap={1}>
        <Text fontWeight="medium">Hightouch-managed</Text>
        <Tooltip message="Special property used by Hightouch for messaging">
          <InformationIcon />
        </Tooltip>
      </Row>
      {option.propertyType && (
        <Text color="text.secondary" textTransform="capitalize">
          {option.propertyType}
        </Text>
      )}
    </Column>
  );
};
